/**
 * React Query hooks for the GHL Qualifier admin dashboard.
 *
 * Mirrors the patterns established in `useGHLDashboard.ts`:
 *   - Query keys are namespaced as ['admin', 'ghl-qualifier', ...]
 *   - List feeds refetch every 15s, stats every 60s, with `staleTime` set
 *     a bit lower so manual refreshes feel snappy.
 *   - All RPCs accept ISO timestamps; we convert from `TimeRange` here so
 *     callers don't have to.
 *   - Hooks are gated by an `enabled` flag so inactive tabs stay dormant.
 *
 * The dashboard reads exclusively — no qualifier runtime is required. Tables
 * and RPCs are queried via the standard authenticated supabase-js client
 * with the existing admin RLS posture.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { TimeRange } from '@/components/ghl-dashboard/TimeRangeFilter'

const FEED_REFRESH_INTERVAL = 15 * 1000
const STATS_REFRESH_INTERVAL = 60 * 1000
const PAGE_SIZE_DEFAULT = 50

// ---------------------------------------------------------------------------
// Result row types
// ---------------------------------------------------------------------------

export interface QualifierLocationOverviewRow {
  readonly location_id: string
  readonly location_name: string
  readonly qualifier_v1_enabled: boolean
  readonly data_pipeline_enabled: boolean
  readonly qualified_total: number
  /**
   * Precise count of rows where qualification_result = 'qualified' — added
   * in migration 20260515100000 so the dashboard doesn't have to derive it
   * via `total - unqualified` (which silently miscounts pending rows).
   * Optional in the type because older RPC versions don't return it; the
   * dashboard falls back to the derived formula when missing.
   */
  readonly qualified_qualified?: number
  readonly qualified_unqualified: number
  readonly qualified_pending: number
  readonly converted_total: number
  readonly converted_completed: number
  readonly converted_in_progress: number
  readonly converted_exhausted: number
  /**
   * Phone/form-call lead counts — added in migration 20260622010000 so
   * phone-call-only locations (which have no qualified/converted rows) still
   * appear in the overview. Optional because older RPC versions omit them.
   */
  readonly form_call_total?: number
  readonly form_call_completed?: number
  readonly form_call_pending?: number
  readonly last_activity_at: string | null
}

export interface QualifiedLeadFeedRow {
  readonly id: string
  readonly contact_id: string
  readonly location_id: string
  readonly contact_name: string | null
  readonly contact_email: string | null
  readonly contact_phone: string | null
  readonly status: string
  readonly qualification_result: string | null
  readonly classification_pattern: string | null
  readonly evidence_quote: string | null
  readonly client_booking_quote: string | null
  readonly confidence: number | null
  readonly tags_applied: string[] | null
  readonly message_count: number
  readonly fire_at: string | null
  readonly error_message: string | null
  readonly created_at: string
  readonly updated_at: string
  readonly total_count: number
}

export interface ConvertedLeadFeedRow {
  readonly id: string
  readonly contact_id: string
  readonly location_id: string
  readonly opportunity_id: string | null
  readonly contact_name: string | null
  readonly contact_email: string | null
  readonly contact_phone: string | null
  readonly status: string
  readonly check_count: number
  readonly max_checks: number
  readonly last_outcome: string | null
  readonly last_evaluated_at: string | null
  readonly last_price_quoted: number | null
  readonly monetary_value_written: number | null
  readonly monetary_value_written_at: string | null
  readonly cancel_reason: string | null
  readonly error_message: string | null
  readonly created_at: string
  readonly updated_at: string
  readonly total_count: number
}

export interface PhoneCallLeadFeedRow {
  readonly id: string
  readonly contact_id: string
  readonly location_id: string
  readonly opportunity_id: string | null
  readonly contact_name: string | null
  readonly contact_email: string | null
  readonly contact_phone: string | null
  readonly status: string
  readonly tags_found: string[] | null
  readonly events_uploaded: string[] | null
  readonly last_outcome: string | null
  readonly last_evaluated_at: string | null
  readonly error_message: string | null
  readonly fire_at: string | null
  readonly created_at: string
  readonly updated_at: string
  readonly total_count: number
}

export interface QualifierStatsByPattern {
  readonly [pattern: string]: number
}

export interface QualifierStatsQualified {
  readonly total: number
  readonly qualified: number
  readonly unqualified: number
  readonly pending: number
  readonly failed: number
  readonly avg_confidence: number
  readonly total_input_tokens: number
  readonly total_output_tokens: number
  readonly by_pattern: QualifierStatsByPattern
}

export interface QualifierStatsConverted {
  readonly total: number
  readonly completed_converted: number
  readonly completed_exhausted: number
  readonly in_progress: number
  readonly cancelled: number
  readonly failed: number
  readonly monetary_value_total: number
  readonly monetary_value_avg: number
  readonly avg_checks_to_confirm: number
}

/**
 * Whisper transcription roll-up from `ghl_whisper_usage_daily`, returned by
 * `get_ghl_qualifier_stats` since migration
 * 20260522120000_extend_ghl_qualifier_stats_for_cost_dashboards.
 *
 * `total_cost_usd` is authoritative — it's accumulated at write time using
 * the actual provider price the worker saw, which survives provider pricing
 * changes better than recomputing from `total_duration_seconds` in the
 * frontend. The duration field is kept for the "X min transcribed" subtitle.
 */
export interface QualifierStatsWhisper {
  readonly total_duration_seconds: number
  readonly total_cost_usd: number
  readonly total_calls: number
}

export interface QualifierStatsFormCall {
  readonly total: number
  readonly completed: number
  readonly pending: number
  readonly cancelled: number
  readonly failed: number
  readonly phone_call_uploaded: number
  readonly form_submit_uploaded: number
}

export interface QualifierStats {
  readonly qualified: QualifierStatsQualified
  readonly converted: QualifierStatsConverted
  readonly whisper: QualifierStatsWhisper
  /** Phone/form-call lead counts — added in migration 20260622010000. Optional pre-migration. */
  readonly form_call?: QualifierStatsFormCall
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Per-location overview for the Qualifier landing page.
 * Backed by `get_ghl_qualifier_location_overview` RPC.
 */
export function useQualifierLocationOverview(timeRange: TimeRange, enabled = true) {
  return useQuery<QualifierLocationOverviewRow[]>({
    queryKey: [
      'admin',
      'ghl-qualifier',
      'location-overview',
      timeRange.from.toISOString(),
      timeRange.until.toISOString(),
    ],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_ghl_qualifier_location_overview', {
        p_start_date: timeRange.from.toISOString(),
        p_end_date: timeRange.until.toISOString(),
      })

      if (error) throw new Error(error.message)
      return (data ?? []) as unknown as QualifierLocationOverviewRow[]
    },
    refetchInterval: STATS_REFRESH_INTERVAL,
    staleTime: 30 * 1000,
    enabled,
  })
}

/**
 * Aggregate stats for a location (or all locations when locationId is null).
 * Backed by `get_ghl_qualifier_stats` RPC.
 */
export function useQualifierStats(
  timeRange: TimeRange,
  locationId?: string | null,
  enabled = true
) {
  return useQuery<QualifierStats | null>({
    queryKey: [
      'admin',
      'ghl-qualifier',
      'stats',
      timeRange.from.toISOString(),
      timeRange.until.toISOString(),
      locationId ?? '',
    ],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_ghl_qualifier_stats', {
        p_start_date: timeRange.from.toISOString(),
        p_end_date: timeRange.until.toISOString(),
        p_location_id: locationId || null,
      })

      if (error) throw new Error(error.message)
      return data as unknown as QualifierStats
    },
    refetchInterval: STATS_REFRESH_INTERVAL,
    staleTime: 30 * 1000,
    enabled,
  })
}

/**
 * Paginated qualified-leads feed for a location (or all locations).
 * Backed by `get_ghl_qualified_leads_feed` RPC.
 *
 * `outcome` accepts: 'qualified' | 'unqualified' | 'pending' | null (all).
 */
export function useQualifiedLeadsFeed(
  timeRange: TimeRange,
  page: number,
  locationId?: string | null,
  outcome?: string | null,
  pageSize = PAGE_SIZE_DEFAULT,
  enabled = true
) {
  return useQuery<{ data: QualifiedLeadFeedRow[]; count: number }>({
    queryKey: [
      'admin',
      'ghl-qualifier',
      'qualified-feed',
      timeRange.from.toISOString(),
      timeRange.until.toISOString(),
      locationId ?? '',
      outcome ?? '',
      page,
      pageSize,
    ],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_ghl_qualified_leads_feed', {
        p_start_date: timeRange.from.toISOString(),
        p_end_date: timeRange.until.toISOString(),
        p_location_id: locationId || null,
        p_outcome: outcome && outcome !== 'all' ? outcome : null,
        p_limit: pageSize,
        p_offset: page * pageSize,
      })

      if (error) throw new Error(error.message)

      const rows = (data ?? []) as unknown as QualifiedLeadFeedRow[]
      return {
        data: rows,
        // total_count is denormalized into every row by the RPC; pull from the
        // first row so callers can render pagination UI.
        count: rows[0]?.total_count ?? 0,
      }
    },
    refetchInterval: FEED_REFRESH_INTERVAL,
    staleTime: 10 * 1000,
    enabled,
  })
}

/**
 * Paginated converted-leads feed for a location (or all locations).
 * Backed by `get_ghl_converted_leads_feed` RPC.
 *
 * `status` accepts: 'completed_converted' | 'completed_exhausted' |
 *   'in_progress' | 'scheduled' | 'cancelled' | 'failed' | null (all).
 */
export function useConvertedLeadsFeed(
  timeRange: TimeRange,
  page: number,
  locationId?: string | null,
  status?: string | null,
  pageSize = PAGE_SIZE_DEFAULT,
  enabled = true
) {
  return useQuery<{ data: ConvertedLeadFeedRow[]; count: number }>({
    queryKey: [
      'admin',
      'ghl-qualifier',
      'converted-feed',
      timeRange.from.toISOString(),
      timeRange.until.toISOString(),
      locationId ?? '',
      status ?? '',
      page,
      pageSize,
    ],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_ghl_converted_leads_feed', {
        p_start_date: timeRange.from.toISOString(),
        p_end_date: timeRange.until.toISOString(),
        p_location_id: locationId || null,
        p_status: status && status !== 'all' ? status : null,
        p_limit: pageSize,
        p_offset: page * pageSize,
      })

      if (error) throw new Error(error.message)

      const rows = (data ?? []) as unknown as ConvertedLeadFeedRow[]
      return {
        data: rows,
        count: rows[0]?.total_count ?? 0,
      }
    },
    refetchInterval: FEED_REFRESH_INTERVAL,
    staleTime: 10 * 1000,
    enabled,
  })
}

/**
 * Paginated phone/form-call leads feed for a location (or all locations).
 * Backed by `get_ghl_phone_call_leads_feed` RPC (sourced from
 * `ghl_form_call_lead_checks`). This is the contact feed for phone-call-only
 * locations, whose qualified/converted jobs are suppressed.
 *
 * `status` accepts: 'pending' | 'processing' | 'completed' | 'failed' |
 *   'cancelled' | null (all).
 */
export function usePhoneCallLeadsFeed(
  timeRange: TimeRange,
  page: number,
  locationId?: string | null,
  status?: string | null,
  pageSize = PAGE_SIZE_DEFAULT,
  enabled = true
) {
  return useQuery<{ data: PhoneCallLeadFeedRow[]; count: number }>({
    queryKey: [
      'admin',
      'ghl-qualifier',
      'phone-call-feed',
      timeRange.from.toISOString(),
      timeRange.until.toISOString(),
      locationId ?? '',
      status ?? '',
      page,
      pageSize,
    ],
    queryFn: async () => {
      // `get_ghl_phone_call_leads_feed` isn't in the generated supabase types
      // until db:types is regenerated, so call it through a minimal cast —
      // same pattern the other qualifier hooks use for not-yet-typed objects.
      const { data, error } = await (
        supabase.rpc as unknown as (
          fn: string,
          args: Record<string, unknown>
        ) => Promise<{ data: unknown; error: { message: string } | null }>
      )('get_ghl_phone_call_leads_feed', {
        p_start_date: timeRange.from.toISOString(),
        p_end_date: timeRange.until.toISOString(),
        p_location_id: locationId || null,
        p_status: status && status !== 'all' ? status : null,
        p_limit: pageSize,
        p_offset: page * pageSize,
      })

      if (error) throw new Error(error.message)

      const rows = (data ?? []) as unknown as PhoneCallLeadFeedRow[]
      return {
        data: rows,
        count: rows[0]?.total_count ?? 0,
      }
    },
    refetchInterval: FEED_REFRESH_INTERVAL,
    staleTime: 10 * 1000,
    enabled,
  })
}

// ---------------------------------------------------------------------------
// Detail-page hooks (single-row queries)
// ---------------------------------------------------------------------------

export interface QualifiedLeadDetail {
  readonly id: string
  readonly contact_id: string
  readonly location_id: string
  readonly opportunity_id: string | null
  readonly fire_at: string
  readonly status: string
  readonly qualification_result: string | null
  readonly qualification_reason: string | null
  readonly classification_pattern: string | null
  readonly evidence_quote: string | null
  readonly client_booking_quote: string | null
  readonly conversation_summary: string | null
  readonly confidence: number | null
  readonly tags_applied: string[] | null
  readonly message_count: number
  readonly input_tokens: number | null
  readonly output_tokens: number | null
  readonly model_used: string | null
  readonly error_message: string | null
  readonly created_at: string
  readonly updated_at: string
  // Note: lead_type, is_corporate_domain, survey_fields columns still exist
  // on the table from migration 20260509120000 but are no longer populated
  // by the worker (Step 1 is now driven by a contact tag instead of
  // structured inputs). Omitted from this interface so consumers don't
  // accidentally rely on them.
}

export interface QualifierAnalysisRow {
  readonly id: string
  readonly contact_id: string
  readonly conversation_id: string | null
  readonly location_id: string
  readonly opportunity_id: string | null
  readonly trigger_event: string
  readonly booking_detected: boolean
  readonly booking_confidence: number
  readonly price_quoted: number | null
  readonly price_confirmed: boolean
  readonly extracted_fields: Record<string, unknown>
  readonly conversation_summary: string | null
  readonly recommended_action: string
  readonly reasoning: string | null
  readonly raw_messages: unknown[] | null
  readonly raw_claude_response: string | null
  readonly input_tokens: number | null
  readonly output_tokens: number | null
  readonly model_used: string | null
  readonly tags_applied: string[] | null
  readonly message_count: number
  /**
   * Whisper transcription stats — NULL when no call recordings were
   * transcribed during this analysis tick. Persisted by the qualifier
   * worker after invoking gpt-4o-mini-transcribe inline.
   */
  readonly whisper_calls: number | null
  readonly whisper_duration_seconds: number | null
  readonly whisper_audio_bytes: number | null
  readonly whisper_cost_usd: number | null
  readonly created_at: string
  readonly updated_at: string
}

/**
 * Single qualified-lead row + the most recent analysis associated with it.
 * The analysis is queried by `(contact_id, location_id)` because qualifier
 * rows do not store a direct FK into `ghl_conversation_analyses` — the worker
 * writes a fresh analysis row alongside the qualification, and we surface
 * whichever analysis was most recently created for the same contact.
 */
export function useQualifiedLeadDetail(id: string | undefined) {
  return useQuery<{
    qualification: QualifiedLeadDetail
    analysis: QualifierAnalysisRow | null
    contact: {
      first_name: string | null
      last_name: string | null
      full_name: string | null
      email: string | null
      phone: string | null
    } | null
  } | null>({
    queryKey: ['admin', 'ghl-qualifier', 'qualified-detail', id ?? ''],
    queryFn: async () => {
      if (!id) return null

      const { data: qualification, error: qErr } = await supabase
        .from('ghl_qualified_lead_checks')
        .select('*')
        .eq('id', id)
        .maybeSingle()

      if (qErr) throw new Error(qErr.message)
      if (!qualification) return null

      const [analysisResult, contactResult] = await Promise.all([
        supabase
          .from('ghl_conversation_analyses')
          .select('*')
          .eq('contact_id', qualification.contact_id)
          .eq('location_id', qualification.location_id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle(),
        supabase
          .from('ghl_contacts_cache')
          .select('first_name, last_name, full_name, email, phone')
          .eq('contact_id', qualification.contact_id)
          .maybeSingle(),
      ])

      return {
        qualification: qualification as unknown as QualifiedLeadDetail,
        analysis: (analysisResult.data ?? null) as unknown as QualifierAnalysisRow | null,
        contact: contactResult.data ?? null,
      }
    },
    enabled: !!id,
    staleTime: 60 * 1000,
  })
}

/**
 * Outcome of the contact.google_ads_converted_leads_monetary field-write attempt
 * recorded on the lifecycle row. Mirrors the FieldWriteStatus emitted by the
 * qualifier worker on a confirmed-conversion tick. Null when the lifecycle
 * never reached that code path (exhausted, cancelled, in-progress, or
 * tag-pre-check early-exit).
 */
export type ConvertedFieldWriteStatus =
  | 'success'
  | 'skipped_no_price'
  | 'skipped_already_set'
  | 'skipped_already_converted'
  | 'failed_field_missing'
  | 'failed_api_error'

export interface ConvertedLeadDetail {
  readonly id: string
  readonly contact_id: string
  readonly location_id: string
  readonly opportunity_id: string | null
  readonly status: string
  readonly check_count: number
  readonly max_checks: number
  readonly last_outcome: string | null
  readonly last_evaluated_at: string | null
  readonly next_fire_at: string | null
  readonly last_price_quoted: number | null
  readonly monetary_value_written: number | null
  readonly monetary_value_written_at: string | null
  readonly last_analysis_id: string | null
  readonly cancel_reason: string | null
  readonly error_message: string | null
  readonly field_write_status: ConvertedFieldWriteStatus | null
  readonly field_write_error: string | null
  readonly field_write_attempted_at: string | null
  readonly created_at: string
  readonly updated_at: string
}

/**
 * Converted-lead lifecycle row + the full timeline of daily-check analyses
 * for the contact. Each analysis row corresponds to one day's Claude check.
 */
export function useConvertedLeadDetail(id: string | undefined) {
  return useQuery<{
    lifecycle: ConvertedLeadDetail
    timeline: QualifierAnalysisRow[]
    contact: {
      first_name: string | null
      last_name: string | null
      full_name: string | null
      email: string | null
      phone: string | null
    } | null
  } | null>({
    queryKey: ['admin', 'ghl-qualifier', 'converted-detail', id ?? ''],
    queryFn: async () => {
      if (!id) return null

      const { data: lifecycle, error: lErr } = await supabase
        .from('ghl_converted_lead_checks')
        .select('*')
        .eq('id', id)
        .maybeSingle()

      if (lErr) throw new Error(lErr.message)
      if (!lifecycle) return null

      // Pull every conversation_analyses row written for this contact at this
      // location whose trigger event identifies it as a converted-lead check.
      // The worker writes `trigger_event = 'qualifier:converted_lead'` (one
      // row per daily check); ordering ascending lays them out as days 1..N.
      const [timelineResult, contactResult] = await Promise.all([
        supabase
          .from('ghl_conversation_analyses')
          .select('*')
          .eq('contact_id', lifecycle.contact_id)
          .eq('location_id', lifecycle.location_id)
          .like('trigger_event', 'qualifier:converted%')
          .order('created_at', { ascending: true }),
        supabase
          .from('ghl_contacts_cache')
          .select('first_name, last_name, full_name, email, phone')
          .eq('contact_id', lifecycle.contact_id)
          .maybeSingle(),
      ])

      return {
        lifecycle: lifecycle as unknown as ConvertedLeadDetail,
        timeline: (timelineResult.data ?? []) as unknown as QualifierAnalysisRow[],
        contact: contactResult.data ?? null,
      }
    },
    enabled: !!id,
    staleTime: 60 * 1000,
  })
}

export interface PhoneCallLeadDetail {
  readonly id: string
  readonly contact_id: string
  readonly location_id: string
  readonly opportunity_id: string | null
  readonly status: string
  readonly tags_found: string[] | null
  readonly events_uploaded: string[] | null
  readonly last_outcome: string | null
  readonly last_evaluated_at: string | null
  readonly error_message: string | null
  readonly fire_at: string | null
  readonly attempt_count: number
  readonly created_at: string
  readonly updated_at: string
}

/**
 * Phone/form-call lead row (from `ghl_form_call_lead_checks`) + contact
 * identity. Unlike qualified/converted there is no Claude analysis timeline —
 * the form-call job only reads GHL tags and uploads the matching conversion.
 */
export function usePhoneCallLeadDetail(id: string | undefined) {
  return useQuery<{
    lead: PhoneCallLeadDetail
    contact: {
      first_name: string | null
      last_name: string | null
      full_name: string | null
      email: string | null
      phone: string | null
    } | null
  } | null>({
    queryKey: ['admin', 'ghl-qualifier', 'phone-call-detail', id ?? ''],
    queryFn: async () => {
      if (!id) return null

      // `ghl_form_call_lead_checks` isn't in the generated supabase types yet
      // (added in migration 20260529100200); cast the builder, same pattern the
      // other qualifier reads use for not-yet-typed objects.
      const { data: lead, error: lErr } = await (
        supabase.from('ghl_form_call_lead_checks') as unknown as {
          select: (s: string) => {
            eq: (k: string, v: string) => {
              maybeSingle: () => Promise<{
                data: PhoneCallLeadDetail | null
                error: { message: string } | null
              }>
            }
          }
        }
      )
        .select('*')
        .eq('id', id)
        .maybeSingle()

      if (lErr) throw new Error(lErr.message)
      if (!lead) return null

      const { data: contact } = await supabase
        .from('ghl_contacts_cache')
        .select('first_name, last_name, full_name, email, phone')
        .eq('contact_id', lead.contact_id)
        .maybeSingle()

      return { lead, contact: contact ?? null }
    },
    enabled: !!id,
    staleTime: 60 * 1000,
  })
}

// ---------------------------------------------------------------------------
// Cancellation mutations (manual operator action from detail pages)
// ---------------------------------------------------------------------------

/**
 * Cancel a pending/in-flight qualified-lead check.
 * Backed by `cancel_ghl_qualified_lead_check(p_id, p_reason)` RPC.
 * On success, invalidates the detail + feed + overview queries so the UI
 * picks up the new 'cancelled' status without a manual refresh.
 */
export function useCancelQualifiedLead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: { id: string; reason?: string }) => {
      const { data, error } = await supabase.rpc('cancel_ghl_qualified_lead_check', {
        p_id: input.id,
        p_reason: input.reason ?? 'manual',
      })
      if (error) throw new Error(error.message)
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'ghl-qualifier'] })
    },
  })
}

/**
 * Cancel a pending/in-flight converted-lead check.
 * Backed by `cancel_ghl_converted_lead_check(p_id, p_reason)` RPC.
 */
export function useCancelConvertedLead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: { id: string; reason?: string }) => {
      const { data, error } = await supabase.rpc('cancel_ghl_converted_lead_check', {
        p_id: input.id,
        p_reason: input.reason ?? 'manual',
      })
      if (error) throw new Error(error.message)
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'ghl-qualifier'] })
    },
  })
}

// ---------------------------------------------------------------------------
// Retry mutations (manual operator rerun of failed checks)
// ---------------------------------------------------------------------------

export interface RetryQualifierCheckResult {
  readonly success: boolean
  /** The check row after the retry RPC ran (pending if re-queued). */
  readonly row: Record<string, unknown>
  /** False when the row wasn't in 'failed' (RPC no-op'd). */
  readonly retried: boolean
  /**
   * True when the qualifier's drain endpoint processed the row synchronously.
   * False means the row is re-queued and the ~2-minute cron will pick it up.
   */
  readonly immediate: boolean
  readonly error?: string
}

/**
 * Rerun a failed qualifier check via the `retry-qualifier-check` edge
 * function: it resets the row with the admin-gated retry RPC (using the
 * caller's JWT), then synchronously triggers the qualifier's drain endpoint
 * so the rerun happens immediately rather than on the next cron tick.
 */
function useRetryQualifierCheck(checkType: 'qualified' | 'converted') {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: { id: string }): Promise<RetryQualifierCheckResult> => {
      const { data, error } = await supabase.functions.invoke('retry-qualifier-check', {
        body: { checkType, id: input.id },
      })
      if (error) throw new Error(error.message)
      const result = data as RetryQualifierCheckResult
      if (!result?.success) throw new Error(result?.error ?? 'Retry failed')
      return result
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'ghl-qualifier'] })
    },
  })
}

/** Rerun a failed qualified-lead check (immediate processing). */
export function useRetryQualifiedLead() {
  return useRetryQualifierCheck('qualified')
}

/** Rerun a failed converted-lead check (immediate processing). */
export function useRetryConvertedLead() {
  return useRetryQualifierCheck('converted')
}

// ---------------------------------------------------------------------------
// Failed jobs feed (cross-location)
// ---------------------------------------------------------------------------
//
// Surfaces every row in either queue table that ended in (or is currently
// experiencing) a failure state. Used by the FailedJobsPanel sheet on the
// location list page so operators can triage failures globally without
// drilling into each location one at a time.
//
// Failure definition:
//   - Qualified Lead: status='failed' (terminal — retries exhausted)
//   - Converted Lead: status='failed' (legacy stuck rows from before the
//                     retry-then-advance fix), OR field_write_status starts
//                     with 'failed_' (confirmation landed but the dollar-value
//                     write didn't — operator needs to reconcile manually)

export interface FailedQualifiedJob {
  readonly id: string
  readonly contact_id: string
  readonly contact_name: string | null
  readonly contact_email: string | null
  readonly location_id: string
  readonly location_name: string
  readonly status: string
  readonly error_message: string | null
  readonly attempt_count: number | null
  readonly updated_at: string
}

export interface FailedConvertedJob {
  readonly id: string
  readonly contact_id: string
  readonly contact_name: string | null
  readonly contact_email: string | null
  readonly location_id: string
  readonly location_name: string
  readonly status: string
  readonly check_count: number
  readonly max_checks: number
  readonly error_message: string | null
  readonly field_write_status: string | null
  readonly field_write_error: string | null
  readonly updated_at: string
}

export function useGHLQualifierFailures(enabled = true) {
  return useQuery<{
    qualified: readonly FailedQualifiedJob[]
    converted: readonly FailedConvertedJob[]
  }>({
    queryKey: ['admin', 'ghl-qualifier', 'failures'],
    queryFn: async () => {
      // Two independent queries — could move to a dedicated RPC if this list
      // grows large enough to warrant server-side joins + pagination.
      const [qResult, cResult] = await Promise.all([
        supabase
          .from('ghl_qualified_lead_checks')
          .select(
            'id, contact_id, location_id, status, error_message, attempt_count, updated_at'
          )
          .eq('status', 'failed')
          .order('updated_at', { ascending: false })
          .limit(200),
        supabase
          .from('ghl_converted_lead_checks')
          .select(
            'id, contact_id, location_id, status, check_count, max_checks, error_message, field_write_status, field_write_error, updated_at'
          )
          // PostgREST `or` syntax: status='failed' OR field_write_status like 'failed_%'
          .or('status.eq.failed,field_write_status.like.failed_%')
          .order('updated_at', { ascending: false })
          .limit(200),
      ])

      if (qResult.error) throw new Error(`qualified failures: ${qResult.error.message}`)
      if (cResult.error) throw new Error(`converted failures: ${cResult.error.message}`)

      const qRows = (qResult.data ?? []) as Array<{
        id: string
        contact_id: string
        location_id: string
        status: string
        error_message: string | null
        attempt_count: number | null
        updated_at: string
      }>
      const cRows = (cResult.data ?? []) as Array<{
        id: string
        contact_id: string
        location_id: string
        status: string
        check_count: number
        max_checks: number
        error_message: string | null
        field_write_status: string | null
        field_write_error: string | null
        updated_at: string
      }>

      // Collect unique contact + location IDs for batched lookups. Using `Set`
      // de-dupes when the same contact appears in both queues.
      const contactIds = Array.from(
        new Set([...qRows.map((r) => r.contact_id), ...cRows.map((r) => r.contact_id)])
      )
      const locationIds = Array.from(
        new Set([...qRows.map((r) => r.location_id), ...cRows.map((r) => r.location_id)])
      )

      const [contactsResult, locationsResult] = await Promise.all([
        contactIds.length > 0
          ? supabase
              .from('ghl_contacts_cache')
              .select('contact_id, full_name, email')
              .in('contact_id', contactIds)
          : Promise.resolve({
              data: [] as Array<{
                contact_id: string
                full_name: string | null
                email: string | null
              }>,
              error: null,
            }),
        locationIds.length > 0
          ? supabase
              .from('ghl_location_names')
              .select('location_id, name')
              .in('location_id', locationIds)
          : Promise.resolve({
              data: [] as Array<{ location_id: string; name: string }>,
              error: null,
            }),
      ])

      const contactMap = new Map<
        string,
        { full_name: string | null; email: string | null }
      >()
      for (const c of contactsResult.data ?? []) {
        contactMap.set(c.contact_id, { full_name: c.full_name, email: c.email })
      }
      const locationMap = new Map<string, string>()
      for (const l of locationsResult.data ?? []) {
        locationMap.set(l.location_id, l.name)
      }

      const qualified: FailedQualifiedJob[] = qRows.map((r) => ({
        id: r.id,
        contact_id: r.contact_id,
        contact_name: contactMap.get(r.contact_id)?.full_name ?? null,
        contact_email: contactMap.get(r.contact_id)?.email ?? null,
        location_id: r.location_id,
        location_name: locationMap.get(r.location_id) ?? r.location_id,
        status: r.status,
        error_message: r.error_message,
        attempt_count: r.attempt_count,
        updated_at: r.updated_at,
      }))

      const converted: FailedConvertedJob[] = cRows.map((r) => ({
        id: r.id,
        contact_id: r.contact_id,
        contact_name: contactMap.get(r.contact_id)?.full_name ?? null,
        contact_email: contactMap.get(r.contact_id)?.email ?? null,
        location_id: r.location_id,
        location_name: locationMap.get(r.location_id) ?? r.location_id,
        status: r.status,
        check_count: r.check_count,
        max_checks: r.max_checks,
        error_message: r.error_message,
        field_write_status: r.field_write_status,
        field_write_error: r.field_write_error,
        updated_at: r.updated_at,
      }))

      return { qualified, converted }
    },
    enabled,
    refetchInterval: 60_000,
    staleTime: 30_000,
  })
}

// ---------------------------------------------------------------------------
// Google Ads conversion delivery settings (per-location config)
// ---------------------------------------------------------------------------
//
// New tables/RPCs (ghl_google_ads_settings, ghl_google_ads_conversions,
// retry_ghl_google_ads_conversion) aren't in the generated Database types
// until `npm run db:types` runs post-migration, so these calls cast through
// `unknown` — same pattern as the middleware's LocationSettingsDialog.

const GHL_QUALIFIER_URL = import.meta.env.VITE_GHL_QUALIFIER_URL || null

export type GoogleAdsDeliveryMethod = 'ghl' | 'google_ads'

/**
 * The four Google Ads conversion events the qualifier can upload. `qualified`
 * and `converted` are decided by Claude; `form_submit` and `phone_call` are
 * read off the GHL-applied `new survey lead` / `new phone lead` tags.
 */
export type QualifierConversionEventType =
  | 'qualified'
  | 'converted'
  | 'form_submit'
  | 'phone_call'

export interface QualifierGoogleAdsSettingsRow {
  readonly conversion_delivery_method: GoogleAdsDeliveryMethod
  readonly google_ads_customer_id: string | null
  readonly google_ads_conversion_action_qualified: string | null
  readonly google_ads_conversion_action_converted: string | null
  readonly google_ads_conversion_action_form_submit: string | null
  readonly google_ads_conversion_action_phone_call: string | null
  readonly test_mode_enabled: boolean
  readonly test_button_enabled: boolean
  readonly phone_call_only: boolean
}

export function useQualifierGoogleAdsSettings(locationId: string, enabled = true) {
  return useQuery<QualifierGoogleAdsSettingsRow | null>({
    queryKey: ['admin', 'ghl-qualifier', 'google-ads-settings', locationId],
    enabled: enabled && !!locationId,
    queryFn: async () => {
      const { data, error } = await (supabase.from('ghl_google_ads_settings') as unknown as {
        select: (s: string) => {
          eq: (k: string, v: string) => {
            maybeSingle: () => Promise<{
              data: QualifierGoogleAdsSettingsRow | null
              error: { message: string } | null
            }>
          }
        }
      })
        .select(
          'conversion_delivery_method, google_ads_customer_id, ' +
            'google_ads_conversion_action_qualified, google_ads_conversion_action_converted, ' +
            'google_ads_conversion_action_form_submit, google_ads_conversion_action_phone_call, ' +
            'test_mode_enabled, test_button_enabled, phone_call_only'
        )
        .eq('location_id', locationId)
        .maybeSingle()
      if (error) throw new Error(error.message)
      return data
    },
  })
}

export interface UpsertQualifierGoogleAdsSettingsInput {
  readonly locationId: string
  readonly deliveryMethod: GoogleAdsDeliveryMethod
  readonly customerId: string | null
  readonly conversionActionQualified: string | null
  readonly conversionActionConverted: string | null
  readonly conversionActionFormSubmit: string | null
  readonly conversionActionPhoneCall: string | null
  readonly testModeEnabled: boolean
  readonly testButtonEnabled: boolean
  readonly phoneCallOnly: boolean
}

export function useUpsertQualifierGoogleAdsSettings() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: UpsertQualifierGoogleAdsSettingsInput) => {
      const payload = {
        location_id: input.locationId,
        conversion_delivery_method: input.deliveryMethod,
        google_ads_customer_id: input.customerId,
        google_ads_conversion_action_qualified: input.conversionActionQualified,
        google_ads_conversion_action_converted: input.conversionActionConverted,
        google_ads_conversion_action_form_submit: input.conversionActionFormSubmit,
        google_ads_conversion_action_phone_call: input.conversionActionPhoneCall,
        test_mode_enabled: input.testModeEnabled,
        test_button_enabled: input.testButtonEnabled,
        phone_call_only: input.phoneCallOnly,
      }
      const { error } = await (supabase.from('ghl_google_ads_settings') as unknown as {
        upsert: (
          p: Record<string, unknown>,
          o: { onConflict: string }
        ) => Promise<{ error: { message: string } | null }>
      }).upsert(payload, { onConflict: 'location_id' })
      if (error) throw new Error(error.message)

      // Best-effort: bust the qualifier's 60s in-process settings cache so the
      // change takes effect immediately. Non-fatal — the TTL covers it anyway.
      if (GHL_QUALIFIER_URL) {
        await fetch(`${GHL_QUALIFIER_URL}/api/internal/invalidate-settings`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ locationId: input.locationId }),
          signal: AbortSignal.timeout(5_000),
        }).catch(() => {
          /* ignore — cache expires within 60s regardless */
        })
      }
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({
        queryKey: ['admin', 'ghl-qualifier', 'google-ads-settings', vars.locationId],
      })
    },
  })
}

/**
 * Set of location IDs currently on the direct Google Ads flow
 * (`conversion_delivery_method = 'google_ads'`). Drives the "Google Ads" badge
 * on the location cards + table. One light query; polled like the other stats.
 */
export function useQualifierGoogleAdsFlowMap(enabled = true) {
  return useQuery<Set<string>>({
    queryKey: ['admin', 'ghl-qualifier', 'google-ads-flow-map'],
    enabled,
    refetchInterval: STATS_REFRESH_INTERVAL,
    staleTime: 30 * 1000,
    queryFn: async () => {
      const { data, error } = await (supabase.from('ghl_google_ads_settings') as unknown as {
        select: (s: string) => {
          eq: (
            k: string,
            v: string
          ) => Promise<{
            data: Array<{ location_id: string }> | null
            error: { message: string } | null
          }>
        }
      })
        .select('location_id')
        .eq('conversion_delivery_method', 'google_ads')
      if (error) throw new Error(error.message)
      return new Set((data ?? []).map((r) => r.location_id))
    },
  })
}

/**
 * Set of location IDs in Google Ads "test mode" — i.e. BOTH per-location
 * toggles on: `test_button_enabled` (Builder shows the Test upload button)
 * AND `test_mode_enabled` (cron suppresses real auto-uploads, tags only).
 * Drives the "Test Mode" badge on the location cards. One light query, polled
 * like the other stats.
 */
export function useQualifierTestModeMap(enabled = true) {
  return useQuery<Set<string>>({
    queryKey: ['admin', 'ghl-qualifier', 'test-mode-map'],
    enabled,
    refetchInterval: STATS_REFRESH_INTERVAL,
    staleTime: 30 * 1000,
    queryFn: async () => {
      const { data, error } = await (supabase.from('ghl_google_ads_settings') as unknown as {
        select: (s: string) => {
          eq: (
            k: string,
            v: boolean
          ) => {
            eq: (
              k: string,
              v: boolean
            ) => Promise<{
              data: Array<{ location_id: string }> | null
              error: { message: string } | null
            }>
          }
        }
      })
        .select('location_id')
        .eq('test_button_enabled', true)
        .eq('test_mode_enabled', true)
      if (error) throw new Error(error.message)
      return new Set((data ?? []).map((r) => r.location_id))
    },
  })
}

export interface BulkGoogleAdsSettingsRow {
  readonly location_id: string
  readonly google_ads_customer_id: string
  readonly google_ads_conversion_action_qualified: string | null
  readonly google_ads_conversion_action_converted: string | null
  readonly google_ads_conversion_action_form_submit: string | null
  readonly google_ads_conversion_action_phone_call: string | null
  readonly phone_call_only: boolean
}

/**
 * Bulk-enable the Google Ads flow for many locations from a CSV import.
 *
 * One array `upsert` (single PostgREST round-trip) sets each row to
 * `conversion_delivery_method = 'google_ads'` + writes its customer ID and the
 * conversion-action IDs present. `test_mode_enabled` / `test_button_enabled`
 * are intentionally omitted unless `testMode` is set, so existing per-location
 * test settings are preserved on update (and default `false` on insert). After
 * the write we best-effort bust each location's 60s settings cache.
 */
export function useBulkUpsertQualifierGoogleAdsSettings() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      rows: readonly BulkGoogleAdsSettingsRow[]
      testMode: boolean
    }): Promise<{ count: number }> => {
      const payload = input.rows.map((r) => ({
        location_id: r.location_id,
        conversion_delivery_method: 'google_ads' as const,
        google_ads_customer_id: r.google_ads_customer_id,
        google_ads_conversion_action_qualified: r.google_ads_conversion_action_qualified,
        google_ads_conversion_action_converted: r.google_ads_conversion_action_converted,
        google_ads_conversion_action_form_submit: r.google_ads_conversion_action_form_submit,
        google_ads_conversion_action_phone_call: r.google_ads_conversion_action_phone_call,
        phone_call_only: r.phone_call_only,
        ...(input.testMode ? { test_mode_enabled: true } : {}),
      }))

      const { error } = await (supabase.from('ghl_google_ads_settings') as unknown as {
        upsert: (
          p: Record<string, unknown>[],
          o: { onConflict: string }
        ) => Promise<{ error: { message: string } | null }>
      }).upsert(payload, { onConflict: 'location_id' })
      if (error) throw new Error(error.message)

      // Best-effort cache-bust per location (60s TTL covers it if these fail).
      if (GHL_QUALIFIER_URL) {
        await Promise.allSettled(
          input.rows.map((r) =>
            fetch(`${GHL_QUALIFIER_URL}/api/internal/invalidate-settings`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ locationId: r.location_id }),
              signal: AbortSignal.timeout(5_000),
            })
          )
        )
      }

      return { count: payload.length }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'ghl-qualifier', 'google-ads-flow-map'] })
      qc.invalidateQueries({ queryKey: ['admin', 'ghl-qualifier', 'google-ads-settings'] })
    },
  })
}

export interface TestUploadResult {
  readonly ok: boolean
  readonly reason?: string
  readonly message?: string
  readonly eventType?: string
  readonly transactionId?: string
  readonly status?: string
  readonly error?: string | null
  readonly validateOnly?: boolean | null
}

/**
 * Test trigger: immediately fire a qualified or converted conversion upload for
 * one contact (bypasses the cron). Calls the qualifier's
 * /api/internal/test-upload-conversion endpoint, which reuses the real cron code
 * path and returns the ledger outcome. The endpoint is gated server-side by the
 * per-location `test_mode_enabled` toggle.
 */
export function useTestUploadConversion() {
  return useMutation({
    mutationFn: async (input: {
      contactId: string
      locationId: string
      eventType: QualifierConversionEventType
      conversionValue?: number | null
    }): Promise<TestUploadResult> => {
      if (!GHL_QUALIFIER_URL) {
        throw new Error('VITE_GHL_QUALIFIER_URL is not configured')
      }
      const resp = await fetch(`${GHL_QUALIFIER_URL}/api/internal/test-upload-conversion`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
        signal: AbortSignal.timeout(20_000),
      })
      const json = (await resp.json().catch(() => ({}))) as TestUploadResult
      if (!resp.ok) throw new Error(json.error || `Request failed (${resp.status})`)
      return json
    },
  })
}

/**
 * Test trigger for form-submit / phone-call conversions. Unlike
 * useTestUploadConversion, the caller doesn't pick an event type — the qualifier
 * detects it server-side from the contact's GHL tags (`new survey lead` →
 * form_submit, `new phone lead` → phone_call) and fires the matching upload. The
 * resolved `eventType` comes back in the result. Same per-location
 * `test_button_enabled` gate as the qualified/converted button.
 */
export function useTestUploadFormCallConversion() {
  return useMutation({
    mutationFn: async (input: {
      contactId: string
      locationId: string
    }): Promise<TestUploadResult> => {
      if (!GHL_QUALIFIER_URL) {
        throw new Error('VITE_GHL_QUALIFIER_URL is not configured')
      }
      const resp = await fetch(`${GHL_QUALIFIER_URL}/api/internal/test-upload-form-call-conversion`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
        signal: AbortSignal.timeout(20_000),
      })
      const json = (await resp.json().catch(() => ({}))) as TestUploadResult
      if (!resp.ok) throw new Error(json.error || `Request failed (${resp.status})`)
      return json
    },
  })
}

// ---------------------------------------------------------------------------
// Google Ads conversion backfill (historical gap remediation)
// ---------------------------------------------------------------------------
//
// Conversions that completed BEFORE a location was switched to google_ads
// delivery were never uploaded (the cron only uploads at the tag-apply
// moment). The Backfill panel lists them via absence-based detection RPCs and
// triggers uploads through the qualifier's backfill endpoint; uploaded
// contacts drop out of the lists automatically on refetch.

export interface BackfillLocation {
  readonly location_id: string
  readonly location_name: string
  readonly google_ads_enabled_at: string
  readonly missing_count: number
}

export interface BackfillContact {
  readonly check_id: string
  readonly contact_id: string
  readonly opportunity_id: string | null
  readonly contact_name: string | null
  readonly contact_email: string | null
  readonly contact_phone: string | null
  readonly converted_at: string
  readonly conversion_value: number | null
}

/**
 * Locations with converted contacts missing a Google Ads upload attempt.
 *
 * `includePreEnablement=false` (default) scopes to conversions completed after
 * each location's Google Ads enable date — the safe window with no
 * double-count risk. In production this is empty: all known misses predate
 * enablement (the GHL workflow owned uploads back then). Pass `true` to widen
 * to all-time and deliberately backfill the historical gap.
 */
export function useBackfillLocations(enabled = true, includePreEnablement = false) {
  return useQuery<BackfillLocation[]>({
    queryKey: ['admin', 'ghl-qualifier', 'backfill', 'locations', includePreEnablement],
    enabled,
    staleTime: 30 * 1000,
    queryFn: async () => {
      const rpc = supabase.rpc as unknown as (
        fn: string,
        args: Record<string, unknown>
      ) => Promise<{ data: BackfillLocation[] | null; error: { message: string } | null }>
      const { data, error } = await rpc('get_ghl_backfill_locations', {
        p_include_pre_enablement: includePreEnablement,
      })
      if (error) throw new Error(error.message)
      return data ?? []
    },
  })
}

/** Missing-upload contacts for one location (Backfill panel drill-down). */
export function useBackfillContacts(
  locationId: string | null,
  enabled = true,
  includePreEnablement = false
) {
  return useQuery<BackfillContact[]>({
    queryKey: [
      'admin',
      'ghl-qualifier',
      'backfill',
      'contacts',
      locationId,
      includePreEnablement,
    ],
    enabled: enabled && !!locationId,
    staleTime: 30 * 1000,
    queryFn: async () => {
      const rpc = supabase.rpc as unknown as (
        fn: string,
        args: Record<string, unknown>
      ) => Promise<{ data: BackfillContact[] | null; error: { message: string } | null }>
      const { data, error } = await rpc('get_ghl_backfill_contacts', {
        p_location_id: locationId,
        p_include_pre_enablement: includePreEnablement,
      })
      if (error) throw new Error(error.message)
      return data ?? []
    },
  })
}

export interface BackfillUploadResult extends TestUploadResult {
  readonly eventTimestamp?: string
}

/**
 * Upload one missed conversion via the qualifier's backfill endpoint. The
 * server derives opportunity id, conversion value, and the ORIGINAL conversion
 * timestamp from the check row — the client only names the contact.
 * Idempotent (transaction_id dedup at the ledger and at Google).
 */
export function useBackfillConversion() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      contactId: string
      locationId: string
    }): Promise<BackfillUploadResult> => {
      if (!GHL_QUALIFIER_URL) {
        throw new Error('VITE_GHL_QUALIFIER_URL is not configured')
      }
      const resp = await fetch(`${GHL_QUALIFIER_URL}/api/internal/backfill-conversion`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
        signal: AbortSignal.timeout(30_000),
      })
      const json = (await resp.json().catch(() => ({}))) as BackfillUploadResult
      if (!resp.ok) throw new Error(json.error || json.message || `Request failed (${resp.status})`)
      return json
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'ghl-qualifier', 'backfill'] })
    },
  })
}

/** One conversion action returned by the fetch endpoint (for the picker). */
export interface FetchedConversionAction {
  readonly id: string
  readonly name: string
  readonly category: string
  readonly status: string
  readonly type: string
}

/** Best-effort name match for each of the four qualifier conversion slots. */
export interface FetchedConversionActionMatches {
  readonly qualified: string | null
  readonly converted: string | null
  readonly formSubmit: string | null
  readonly phoneCall: string | null
}

export interface FetchConversionActionsResult {
  readonly ok: boolean
  readonly customerId?: string
  readonly actions?: FetchedConversionAction[]
  readonly matched?: FetchedConversionActionMatches
  readonly error?: string
  readonly reason?: string
}

/**
 * Fetch a customer account's conversion actions from Google Ads (READ-ONLY) so
 * the settings dialog can auto-fill the four action-ID fields by name and offer
 * a per-field picker for the rest. Calls the qualifier's
 * /api/internal/fetch-conversion-actions endpoint, which issues a single GAQL
 * SELECT against the given customer ID under the shared MCC.
 */
export function useFetchConversionActions() {
  return useMutation({
    mutationFn: async (input: { customerId: string }): Promise<FetchConversionActionsResult> => {
      if (!GHL_QUALIFIER_URL) {
        throw new Error('VITE_GHL_QUALIFIER_URL is not configured')
      }
      const resp = await fetch(`${GHL_QUALIFIER_URL}/api/internal/fetch-conversion-actions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
        // Google Ads queries can take a few seconds, especially the first call
        // after a cold start (OAuth token mint). 30s gives ample headroom.
        signal: AbortSignal.timeout(30_000),
      })
      const json = (await resp.json().catch(() => ({}))) as FetchConversionActionsResult
      if (!resp.ok) throw new Error(json.error || `Request failed (${resp.status})`)
      return json
    },
  })
}

/** Manual re-run of a failed Google Ads upload (resets the row to pending). */
export function useRetryGoogleAdsConversion() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: { id: string }) => {
      const rpc = supabase.rpc as unknown as (
        fn: string,
        args: Record<string, unknown>
      ) => Promise<{ data: unknown; error: { message: string } | null }>
      const { data, error } = await rpc('retry_ghl_google_ads_conversion', { p_id: input.id })
      if (error) throw new Error(error.message)
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'ghl-qualifier'] })
    },
  })
}

// ---------------------------------------------------------------------------
// Per-contact upload status (powers the dashboard's UploadStatusBadge column)
// ---------------------------------------------------------------------------

export interface ContactGoogleAdsStatus {
  readonly status: string
  readonly validate_only: boolean | null
  readonly uploaded_at: string | null
  readonly error: string | null
  readonly attempt_count: number | null
  readonly updated_at: string
}

/**
 * Fetch the latest ghl_google_ads_conversions row for each contact for one
 * event type. Matched by `contact_id + event_type`, NOT by a reconstructed
 * transaction_id: the `converted` event keys its transaction_id on the
 * opportunity_id (`{opportunity_id}_converted`), so a `{contact_id}_converted`
 * lookup silently missed every converted upload and rendered a false "—".
 * One batched `.in()` query per page avoids the N+1 trap; React Query
 * deep-compares the key so identical contact-id arrays reuse the cache.
 */
export function useContactGoogleAdsStatus(
  contactIds: readonly string[],
  eventType: QualifierConversionEventType,
  enabled = true
) {
  return useQuery<Record<string, ContactGoogleAdsStatus>>({
    queryKey: ['admin', 'ghl-qualifier', 'google-ads-status', eventType, contactIds],
    enabled: enabled && contactIds.length > 0,
    refetchInterval: 15_000,
    staleTime: 5_000,
    queryFn: async () => {
      const { data, error } = await (supabase
        .from('ghl_google_ads_conversions') as unknown as {
          select: (s: string) => {
            eq: (k: string, v: string) => {
              in: (k: string, v: string[]) => Promise<{
                data: Array<{
                  contact_id: string
                  status: string
                  validate_only: boolean | null
                  uploaded_at: string | null
                  error: string | null
                  attempt_count: number | null
                  updated_at: string
                }> | null
                error: { message: string } | null
              }>
            }
          }
        })
        .select('contact_id, status, validate_only, uploaded_at, error, attempt_count, updated_at')
        .eq('event_type', eventType)
        .in('contact_id', contactIds as string[])
      if (error) throw new Error(error.message)

      // A contact could in theory have >1 row for the same event type (e.g. a
      // contact-keyed backfill alongside an opportunity-keyed cron upload) —
      // keep the most recently updated one.
      const map: Record<string, ContactGoogleAdsStatus> = {}
      for (const r of data ?? []) {
        const existing = map[r.contact_id]
        if (existing && existing.updated_at >= r.updated_at) continue
        map[r.contact_id] = {
          status: r.status,
          validate_only: r.validate_only,
          uploaded_at: r.uploaded_at,
          error: r.error,
          attempt_count: r.attempt_count,
          updated_at: r.updated_at,
        }
      }
      return map
    },
  })
}

// ---------------------------------------------------------------------------
// Per-contact upload detail (single row) — powers the GoogleAdsUploadDetail
// card on the qualified / converted lead detail pages.
// ---------------------------------------------------------------------------

export interface ContactGoogleAdsConversion {
  readonly id: string
  readonly transaction_id: string
  readonly location_id: string
  readonly contact_id: string
  readonly opportunity_id: string | null
  readonly event_type: QualifierConversionEventType
  readonly status: string
  readonly validate_only: boolean | null
  readonly attempt_count: number | null
  readonly max_attempts: number | null
  readonly next_retry_at: string | null
  readonly last_attempt_at: string | null
  readonly uploaded_at: string | null
  readonly error: string | null
  readonly response_body: string | null
  readonly response_status: number | null
  readonly request_id: string | null
  readonly event_payload: unknown
  readonly conversion_action_id: string | null
  readonly google_ads_customer_id: string | null
  readonly login_customer_id: string | null
  readonly event_timestamp: string | null
  readonly gclid: string | null
  readonly had_email: boolean | null
  readonly had_phone: boolean | null
  readonly conversion_value: number | null
  readonly currency: string | null
  readonly created_at: string
  readonly updated_at: string
}

/**
 * Fetch the single ledger row for one contact + event type. Matched by
 * `contact_id + event_type` (ordered newest-first) rather than a reconstructed
 * `{contactId}_{eventType}` transaction_id — the `converted` event keys its
 * transaction_id on the opportunity_id, so the old lookup never found
 * converted uploads and the detail card showed no upload.
 */
export function useContactGoogleAdsConversion(
  contactId: string,
  eventType: QualifierConversionEventType,
  enabled = true
) {
  return useQuery<ContactGoogleAdsConversion | null>({
    queryKey: ['admin', 'ghl-qualifier', 'google-ads-conversion', contactId, eventType],
    enabled: enabled && !!contactId,
    refetchInterval: 15_000,
    staleTime: 5_000,
    queryFn: async () => {
      const { data, error } = await (supabase
        .from('ghl_google_ads_conversions') as unknown as {
          select: (s: string) => {
            eq: (k: string, v: string) => {
              eq: (k: string, v: string) => {
                order: (
                  k: string,
                  o: { ascending: boolean }
                ) => {
                  limit: (n: number) => {
                    maybeSingle: () => Promise<{
                      data: ContactGoogleAdsConversion | null
                      error: { message: string } | null
                    }>
                  }
                }
              }
            }
          }
        })
        .select(
          'id, transaction_id, location_id, contact_id, opportunity_id, event_type, ' +
            'status, validate_only, attempt_count, max_attempts, next_retry_at, last_attempt_at, ' +
            'uploaded_at, error, response_body, response_status, request_id, event_payload, ' +
            'conversion_action_id, google_ads_customer_id, login_customer_id, event_timestamp, ' +
            'gclid, had_email, had_phone, conversion_value, currency, created_at, updated_at'
        )
        .eq('contact_id', contactId)
        .eq('event_type', eventType)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle()
      if (error) throw new Error(error.message)
      return data
    },
  })
}

// ---------------------------------------------------------------------------
// Form/phone-call lead check (the 1h job row) — powers the FormCallCheckDetail
// card on the qualified-lead detail page.
// ---------------------------------------------------------------------------

export interface FormCallLeadCheck {
  readonly id: string
  readonly contact_id: string
  readonly location_id: string
  readonly opportunity_id: string | null
  readonly fire_at: string
  readonly status: string
  readonly tags_found: readonly string[] | null
  readonly events_uploaded: readonly string[] | null
  readonly last_outcome: string | null
  readonly attempt_count: number | null
  readonly last_attempt_at: string | null
  readonly last_evaluated_at: string | null
  readonly next_retry_at: string | null
  /** Reused to carry the per-event skip reason when a tag matched but didn't upload. */
  readonly error_message: string | null
  readonly created_at: string
  readonly updated_at: string
}

/**
 * Fetch the single ghl_form_call_lead_checks row for a contact. Used on the
 * qualified-lead detail page to show whether the 1h form/phone-call check ran,
 * which tags it matched, which events uploaded, and — when nothing uploaded —
 * the reason (e.g. "form_submit: no conversion action ID configured").
 */
export function useFormCallLeadCheck(contactId: string, enabled = true) {
  return useQuery<FormCallLeadCheck | null>({
    queryKey: ['admin', 'ghl-qualifier', 'form-call-check', contactId],
    enabled: enabled && !!contactId,
    refetchInterval: 15_000,
    staleTime: 5_000,
    queryFn: async () => {
      const { data, error } = await (supabase
        .from('ghl_form_call_lead_checks') as unknown as {
          select: (s: string) => {
            eq: (k: string, v: string) => {
              maybeSingle: () => Promise<{
                data: FormCallLeadCheck | null
                error: { message: string } | null
              }>
            }
          }
        })
        .select(
          'id, contact_id, location_id, opportunity_id, fire_at, status, tags_found, ' +
            'events_uploaded, last_outcome, attempt_count, last_attempt_at, last_evaluated_at, ' +
            'next_retry_at, error_message, created_at, updated_at'
        )
        .eq('contact_id', contactId)
        .maybeSingle()
      if (error) throw new Error(error.message)
      return data
    },
  })
}

// ---------------------------------------------------------------------------
// Failed Google Ads upload feed (cross-location) for the FailedJobsPanel
// ---------------------------------------------------------------------------

export interface FailedGoogleAdsUpload {
  readonly id: string
  readonly contact_id: string
  readonly contact_name: string | null
  readonly location_id: string
  readonly location_name: string
  readonly event_type: string
  readonly status: string
  readonly error: string | null
  readonly attempt_count: number | null
  readonly updated_at: string
}

export function useFailedGoogleAdsUploads(enabled = true) {
  return useQuery<readonly FailedGoogleAdsUpload[]>({
    queryKey: ['admin', 'ghl-qualifier', 'google-ads-failures'],
    enabled,
    refetchInterval: 60_000,
    staleTime: 30_000,
    queryFn: async () => {
      const { data, error } = await (supabase.from('ghl_google_ads_conversions') as unknown as {
        select: (s: string) => {
          eq: (k: string, v: string) => {
            order: (
              c: string,
              o: { ascending: boolean }
            ) => {
              limit: (n: number) => Promise<{
                data: Array<{
                  id: string
                  contact_id: string
                  location_id: string
                  event_type: string
                  status: string
                  error: string | null
                  attempt_count: number | null
                  updated_at: string
                }> | null
                error: { message: string } | null
              }>
            }
          }
        }
      })
        .select('id, contact_id, location_id, event_type, status, error, attempt_count, updated_at')
        .eq('status', 'failed')
        .order('updated_at', { ascending: false })
        .limit(200)
      if (error) throw new Error(error.message)

      const rows = data ?? []
      if (rows.length === 0) return []

      const contactIds = Array.from(new Set(rows.map((r) => r.contact_id)))
      const locationIds = Array.from(new Set(rows.map((r) => r.location_id)))

      const [contactsResult, locationsResult] = await Promise.all([
        supabase.from('ghl_contacts_cache').select('contact_id, full_name').in('contact_id', contactIds),
        supabase.from('ghl_location_names').select('location_id, name').in('location_id', locationIds),
      ])

      const contactMap = new Map<string, string | null>()
      for (const c of (contactsResult.data ?? []) as Array<{ contact_id: string; full_name: string | null }>) {
        contactMap.set(c.contact_id, c.full_name)
      }
      const locationMap = new Map<string, string>()
      for (const l of (locationsResult.data ?? []) as Array<{ location_id: string; name: string }>) {
        locationMap.set(l.location_id, l.name)
      }

      return rows.map((r) => ({
        id: r.id,
        contact_id: r.contact_id,
        contact_name: contactMap.get(r.contact_id) ?? null,
        location_id: r.location_id,
        location_name: locationMap.get(r.location_id) ?? r.location_id,
        event_type: r.event_type,
        status: r.status,
        error: r.error,
        attempt_count: r.attempt_count,
        updated_at: r.updated_at,
      }))
    },
  })
}

